import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Megaphone, 
  Play, 
  Pause, 
  Trash2,
  Eye,
  Phone,
  MessageSquare,
  Mail,
  Sparkles,
  Users,
  ArrowRight,
  ChevronRight
} from 'lucide-react';

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const navigate = useNavigate();
  
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    message: '',
    contact_ids: [],
    fallback_channels: ['sms', 'email', 'whatsapp']
  });
  
  const [aiPrompt, setAiPrompt] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, contactsRes] = await Promise.all([
        api.get('/campaigns'),
        api.get('/contacts')
      ]);
      setCampaigns(campaignsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    
    if (newCampaign.message.length > 157) {
      toast.error('Mesaj 157 karakteri geçemez');
      return;
    }
    
    if (newCampaign.contact_ids.length === 0) {
      toast.error('En az bir kişi seçmelisiniz');
      return;
    }
    
    try {
      const response = await api.post('/campaigns', newCampaign);
      toast.success('Kampanya oluşturuldu');
      setIsCreateDialogOpen(false);
      setNewCampaign({
        name: '',
        message: '',
        contact_ids: [],
        fallback_channels: ['sms', 'email', 'whatsapp']
      });
      fetchData();
      navigate(`/campaigns/${response.data.id}`);
    } catch (error) {
      toast.error('Kampanya oluşturulamadı');
    }
  };

  const handleGenerateMessage = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Lütfen bir konu girin');
      return;
    }
    
    setIsGenerating(true);
    try {
      const response = await api.post('/ai/generate-message', {
        prompt: aiPrompt,
        max_length: 157
      });
      setNewCampaign({ ...newCampaign, message: response.data.message });
      toast.success('Mesaj oluşturuldu');
    } catch (error) {
      toast.error('Mesaj oluşturulamadı');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/start`);
      toast.success('Kampanya başlatıldı');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kampanya başlatılamadı');
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/pause`);
      toast.success('Kampanya duraklatıldı');
      fetchData();
    } catch (error) {
      toast.error('Kampanya durdurulamadı');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Bu kampanyayı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success('Kampanya silindi');
      fetchData();
    } catch (error) {
      toast.error('Kampanya silinemedi');
    }
  };

  const toggleContact = (contactId) => {
    setNewCampaign(prev => ({
      ...prev,
      contact_ids: prev.contact_ids.includes(contactId)
        ? prev.contact_ids.filter(id => id !== contactId)
        : [...prev.contact_ids, contactId]
    }));
  };

  const toggleFallback = (channel) => {
    setNewCampaign(prev => ({
      ...prev,
      fallback_channels: prev.fallback_channels.includes(channel)
        ? prev.fallback_channels.filter(c => c !== channel)
        : [...prev.fallback_channels, channel]
    }));
  };

  const selectAllContacts = () => {
    setNewCampaign(prev => ({
      ...prev,
      contact_ids: contacts.map(c => c.id)
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Taslak', className: 'bg-zinc-100 text-zinc-800 border-zinc-200' },
      active: { label: 'Aktif', className: 'bg-emerald-100 text-emerald-800 border-emerald-200 status-pulse' },
      paused: { label: 'Duraklatıldı', className: 'bg-amber-100 text-amber-800 border-amber-200' },
      completed: { label: 'Tamamlandı', className: 'bg-blue-100 text-blue-800 border-blue-200' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const charCount = newCampaign.message.length;
  const charClass = charCount > 150 ? 'char-danger' : charCount > 130 ? 'char-warning' : 'char-safe';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Kampanyalar</h1>
          <p className="text-muted-foreground">Sesli arama kampanyalarınızı yönetin</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-campaign-btn">
              <Plus className="w-4 h-4 mr-2" />
              Kampanya Oluştur
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Yeni Kampanya</DialogTitle>
              <DialogDescription>
                Sesli arama kampanyası oluşturun. Ulaşılamayan kişilere otomatik fallback yapılır.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCampaign}>
              <div className="space-y-6 py-4">
                {/* Campaign Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Kampanya Adı *</Label>
                  <Input
                    id="name"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                    placeholder="Örn: Ocak Ayı Bilgilendirme"
                    required
                    data-testid="campaign-name-input"
                  />
                </div>

                {/* AI Message Generator */}
                <div className="space-y-2">
                  <Label>AI ile Mesaj Oluştur</Label>
                  <div className="flex gap-2">
                    <Input
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Mesaj konusu (örn: randevu hatırlatma, ödeme bildirimi)"
                      data-testid="ai-prompt-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={handleGenerateMessage}
                      disabled={isGenerating}
                      data-testid="generate-message-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {isGenerating ? 'Oluşturuluyor...' : 'Oluştur'}
                    </Button>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="message">Mesaj İçeriği *</Label>
                    <span className={`text-sm font-mono ${charClass}`}>
                      {charCount}/157
                    </span>
                  </div>
                  <Textarea
                    id="message"
                    value={newCampaign.message}
                    onChange={(e) => setNewCampaign({ ...newCampaign, message: e.target.value.slice(0, 157) })}
                    placeholder="Sesli arama ve SMS/WhatsApp için kullanılacak mesaj"
                    rows={3}
                    required
                    data-testid="campaign-message-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Bu mesaj sesli aramada okunacak ve fallback kanallarında gönderilecektir.
                  </p>
                </div>

                {/* Fallback Channels */}
                <div className="space-y-2">
                  <Label>Fallback Kanalları</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Sesli arama başarısız olursa sırasıyla denenecek kanallar
                  </p>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="sms"
                        checked={newCampaign.fallback_channels.includes('sms')}
                        onCheckedChange={() => toggleFallback('sms')}
                        data-testid="fallback-sms-checkbox"
                      />
                      <label htmlFor="sms" className="flex items-center gap-1 text-sm cursor-pointer">
                        <MessageSquare className="w-4 h-4" /> SMS
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="email"
                        checked={newCampaign.fallback_channels.includes('email')}
                        onCheckedChange={() => toggleFallback('email')}
                        data-testid="fallback-email-checkbox"
                      />
                      <label htmlFor="email" className="flex items-center gap-1 text-sm cursor-pointer">
                        <Mail className="w-4 h-4" /> Email
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="whatsapp"
                        checked={newCampaign.fallback_channels.includes('whatsapp')}
                        onCheckedChange={() => toggleFallback('whatsapp')}
                        data-testid="fallback-whatsapp-checkbox"
                      />
                      <label htmlFor="whatsapp" className="flex items-center gap-1 text-sm cursor-pointer">
                        <MessageSquare className="w-4 h-4" /> WhatsApp
                      </label>
                    </div>
                  </div>
                  
                  {/* Fallback Flow Visualization */}
                  <div className="mt-4 p-4 bg-secondary/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">İletişim Akışı:</p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="flex items-center gap-1 px-2 py-1 bg-primary text-primary-foreground rounded">
                        <Phone className="w-3 h-3" /> Sesli Arama
                      </div>
                      {newCampaign.fallback_channels.length > 0 && (
                        <>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">başarısızsa</span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </>
                      )}
                      {newCampaign.fallback_channels.map((channel, idx) => (
                        <div key={channel} className="flex items-center gap-2">
                          <div className="flex items-center gap-1 px-2 py-1 bg-[#F97316]/10 text-[#F97316] rounded">
                            {channel === 'sms' && <MessageSquare className="w-3 h-3" />}
                            {channel === 'email' && <Mail className="w-3 h-3" />}
                            {channel === 'whatsapp' && <MessageSquare className="w-3 h-3" />}
                            {channel.toUpperCase()}
                          </div>
                          {idx < newCampaign.fallback_channels.length - 1 && (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Contact Selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Kişileri Seç *</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={selectAllContacts}>
                      Tümünü Seç
                    </Button>
                  </div>
                  {contacts.length === 0 ? (
                    <div className="p-4 border border-dashed rounded-lg text-center">
                      <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Henüz kişi yok</p>
                      <Link to="/contacts">
                        <Button variant="link" size="sm">Kişi Ekle</Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                      {contacts.map(contact => (
                        <div
                          key={contact.id}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            newCampaign.contact_ids.includes(contact.id) 
                              ? 'bg-primary/5 border border-primary/20' 
                              : 'hover:bg-secondary'
                          }`}
                          onClick={() => toggleContact(contact.id)}
                          data-testid={`contact-select-${contact.id}`}
                        >
                          <Checkbox
                            checked={newCampaign.contact_ids.includes(contact.id)}
                            onCheckedChange={() => toggleContact(contact.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{contact.name}</p>
                            <p className="text-xs text-muted-foreground">{contact.phone}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {newCampaign.contact_ids.length} kişi seçildi
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" data-testid="save-campaign-btn">Kampanya Oluştur</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Megaphone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-1">Henüz kampanya yok</h3>
            <p className="text-muted-foreground mb-4">
              İlk kampanyanızı oluşturarak sesli arama yapmaya başlayın
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              İlk Kampanyanızı Oluşturun
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign, index) => (
            <Card 
              key={campaign.id} 
              className="card-hover stagger-item"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`campaign-card-${campaign.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{campaign.name}</h3>
                      {getStatusBadge(campaign.status)}
                    </div>
                    <p className="text-muted-foreground text-sm line-clamp-1 mb-3">
                      {campaign.message}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Users className="w-4 h-4" />
                        <span>{campaign.stats?.total || 0} kişi</span>
                      </div>
                      <div className="flex items-center gap-1 text-emerald-600">
                        <Phone className="w-4 h-4" />
                        <span>{campaign.stats?.voice_success || 0} başarılı</span>
                      </div>
                      <div className="flex items-center gap-1 text-[#F97316]">
                        <MessageSquare className="w-4 h-4" />
                        <span>{(campaign.stats?.sms_sent || 0) + (campaign.stats?.email_sent || 0) + (campaign.stats?.whatsapp_sent || 0)} fallback</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {campaign.status === 'draft' && (
                      <Button 
                        size="sm" 
                        onClick={() => handleStartCampaign(campaign.id)}
                        data-testid={`start-campaign-${campaign.id}`}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Başlat
                      </Button>
                    )}
                    {campaign.status === 'active' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handlePauseCampaign(campaign.id)}
                        data-testid={`pause-campaign-${campaign.id}`}
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Duraklat
                      </Button>
                    )}
                    {campaign.status === 'paused' && (
                      <Button 
                        size="sm" 
                        onClick={() => handleStartCampaign(campaign.id)}
                        data-testid={`resume-campaign-${campaign.id}`}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Devam Et
                      </Button>
                    )}
                    <Link to={`/campaigns/${campaign.id}`}>
                      <Button size="sm" variant="outline" data-testid={`view-campaign-${campaign.id}`}>
                        <Eye className="w-4 h-4 mr-1" />
                        Detay
                      </Button>
                    </Link>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDeleteCampaign(campaign.id)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`delete-campaign-${campaign.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
